import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EnfafInputFileDraggerLightComponent } from '../../../../../components/enfaf-input-file-dragger-light/enfaf-input-file-dragger-light.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafTextareaLightComponent } from '../../../../../components/enfaf-textarea-light/enfaf-textarea-light.component';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { getVimeoEmbedUrl } from '../../../../../core/utils/vimeo-url.util';
import { CustomFile, CustomUploadedFile } from '../../../../../models/interfaces/custom_file.interface';
import { AmbassadorExample } from '../../../../../models/interfaces/ambassador.interface';

@Component({
  selector: 'app-modal-ambassador-example',
  imports: [EnfafModalComponent, ReactiveFormsModule, EnfafInputComponent, EnfafTextareaLightComponent, EnfafInputFileDraggerLightComponent],
  templateUrl: './modal-ambassador-example.component.html',
  styleUrl: './modal-ambassador-example.component.scss'
})
export class ModalAmbassadorExampleComponent implements OnInit, OnDestroy {
  protected readonly dialogRef = inject(MatDialogRef);
  protected readonly sanitizer = inject(DomSanitizer);
  protected readonly data: AmbassadorExample | null = inject(MAT_DIALOG_DATA);
  protected readonly fb = inject(FormBuilder);
  protected readonly api = inject(ApiService);
  protected readonly toast = inject(ToastService);

  isLoading = signal(false);
  isUploading = signal(false);
  safeUrl: SafeResourceUrl | null = null;
  selectedType = signal<'video' | 'image'>('video');
  localImageFiles: CustomFile[] = [];
  currentImageFiles: CustomUploadedFile[] = [];

  form = this.fb.group({
    title: this.fb.control<string | null>(null),
    description: this.fb.control<string | null>(null),
    vimeoId: this.fb.control<string | null>(null, [Validators.pattern(/^\d{9,10}$/)]),
    imageUrl: this.fb.control<string | null>(null)
  });

  private _destroyed$ = new Subject<void>();

  ngOnInit(): void {
    if (this.data) {
      this.selectedType.set(this.data.type);
      this.form.patchValue({
        title: this.data.title,
        description: this.data.description ?? null,
        vimeoId: this.data.vimeoId ?? null,
        imageUrl: this.data.imageUrl ?? null
      });
      if (this.data.type === 'video' && this.data.vimeoId) {
        this.safeUrl = this.getVimeoUrl(this.data.vimeoId);
      }
      if (this.data.type === 'image' && this.data.imageUrl) {
        const filename = this.data.imageUrl.split('/').pop() || 'imagen.jpg';
        this.currentImageFiles = [{ filename, path: this.data.imageUrl, presignedUrl: this.data.imageUrl }];
      }
    }

    this.form.controls.vimeoId.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((vimeoId) => {
      if (!vimeoId) {
        this.safeUrl = null;
        return;
      }

      const vimeoUrlRegex = /(?:https?:\/\/)?(?:player\.)?vimeo\.com\/(?:video\/)?([\d]+)/;
      const urlMatch = vimeoId.match(vimeoUrlRegex);
      if (urlMatch && urlMatch[1]) {
        this.form.controls.vimeoId.setValue(urlMatch[1], { emitEvent: false });
        this.safeUrl = this.getVimeoUrl(urlMatch[1]);
        return;
      }

      if (vimeoId.length >= 9) {
        this.safeUrl = this.getVimeoUrl(vimeoId);
      } else {
        this.safeUrl = null;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  get isFormValid(): boolean {
    if (!this.form.controls.title.value?.trim()) return false;
    if (this.selectedType() === 'video') {
      const vimeoId = this.form.controls.vimeoId.value?.trim();
      if (!vimeoId || this.form.controls.vimeoId.invalid) return false;
    }
    if (this.selectedType() === 'image' && !this.form.controls.imageUrl.value?.trim()) return false;
    return true;
  }

  selectType(type: 'video' | 'image'): void {
    if (this.selectedType() === type) return;
    this.selectedType.set(type);
    this.form.controls.vimeoId.setValue(null);
    this.form.controls.imageUrl.setValue(null);
    this.safeUrl = null;
    this.localImageFiles = [];
    this.currentImageFiles = [];
  }

  private getVimeoUrl(vimeoId: string): SafeResourceUrl {
    const url = getVimeoEmbedUrl(vimeoId) ?? '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  async onImageFilesDropped(files: CustomFile[]): Promise<void> {
    this.localImageFiles = files;
    if (!files.length) return;

    const file = files[0].file as File;
    this.isUploading.set(true);
    try {
      const response = await firstValueFrom(this.api.uploadAmbassadorExampleImage(file));
      this.form.controls.imageUrl.setValue(response.url);
      this.currentImageFiles = [];
    } catch {
      this.toast.presentErrorToast('Error al subir la imagen');
      this.localImageFiles = [];
    } finally {
      this.isUploading.set(false);
    }
  }

  onCurrentImageDeleted(): void {
    this.currentImageFiles = [];
    this.form.controls.imageUrl.setValue(null);
  }

  closeModal(): void {
    this.dialogRef.close(false);
  }

  async onConfirm(): Promise<void> {
    if (!this.isFormValid) return;

    const payload: Partial<AmbassadorExample> = {
      title: this.form.controls.title.value!.trim(),
      description: this.form.controls.description.value?.trim() || undefined,
      type: this.selectedType(),
      vimeoId: this.selectedType() === 'video' ? (this.form.controls.vimeoId.value?.trim() ?? undefined) : null,
      imageUrl: this.selectedType() === 'image' ? (this.form.controls.imageUrl.value ?? undefined) : null
    };

    this.isLoading.set(true);
    try {
      if (this.data) {
        await firstValueFrom(this.api.updateAmbassadorExample(this.data.ambassadorExampleId, payload));
        this.toast.presentSuccessToast('Ejemplo actualizado correctamente');
      } else {
        await firstValueFrom(this.api.createAmbassadorExample(payload));
        this.toast.presentSuccessToast('Ejemplo creado correctamente');
      }
      this.dialogRef.close(true);
    } catch {
      this.toast.presentErrorToast('Error al guardar el ejemplo');
    } finally {
      this.isLoading.set(false);
    }
  }
}
